/**
 * Engagement Driver Utility Module
 *
 * Extracts and processes engagement context data (ed-1, ed-2, ed-3) from business scoping.
 * Engagement drivers influence:
 * - Roadmap phasing and urgency multipliers
 * - Threat profile relevance weighting
 * - Report narrative framing
 *
 * They do NOT influence maturity scoring or context weights.
 */

import businessScopingData from '../data/businessScoping.json'

// Narrative templates for different driver types
const NARRATIVE_TEMPLATES = {
  'incident-driven': {
    opening: 'Following a recent security incident, this assessment provides a structured path to strengthen defenses and prevent recurrence.',
    roadmapIntro: 'Given the urgency of your recent incident, we recommend an accelerated remediation approach focusing on immediate threat mitigation.',
    priorityFocus: 'Incident response capabilities and detection controls are elevated in priority.'
  },
  'insurance-driven': {
    opening: 'This assessment supports your cyber insurance requirements by documenting your current security posture and identifying areas for improvement.',
    roadmapIntro: 'Your roadmap prioritizes controls commonly required by cyber insurers, ensuring you meet policy requirements.',
    priorityFocus: 'Controls affecting insurability scores are emphasized, including MFA, backup, and incident response.'
  },
  'governance-driven': {
    opening: 'Per executive mandate, this assessment establishes a security baseline and strategic improvement roadmap for board visibility.',
    roadmapIntro: 'The roadmap is structured to provide clear progress metrics suitable for board-level reporting.',
    priorityFocus: 'Governance, policy, and measurable control improvements are prioritized.'
  },
  'customer-driven': {
    opening: 'This assessment addresses customer and partner security requirements, demonstrating your commitment to protecting their data.',
    roadmapIntro: 'Recommendations prioritize controls that satisfy third-party security questionnaires and contractual obligations.',
    priorityFocus: 'Vendor management, access controls, and data protection are emphasized.'
  },
  'compliance-driven': {
    opening: 'With regulatory deadlines approaching, this assessment maps your current state to compliance requirements and identifies gaps.',
    roadmapIntro: 'The roadmap is aligned to your compliance timeline, prioritizing controls required for audit readiness.',
    priorityFocus: 'Compliance-mapped controls and audit evidence generation are prioritized.'
  },
  'maturity-driven': {
    opening: 'This proactive assessment establishes a baseline and strategic roadmap for continuous security improvement.',
    roadmapIntro: 'Your roadmap follows a maturity-based approach, building foundational controls before advancing capabilities.',
    priorityFocus: 'Foundational controls and process maturity are emphasized for sustainable improvement.'
  },
  'transition-driven': {
    opening: 'This assessment establishes a security baseline during your organizational transition, informing strategic planning.',
    roadmapIntro: 'Recommendations account for organizational change, balancing quick wins with longer-term strategic initiatives.',
    priorityFocus: 'Quick wins and foundational controls are prioritized to establish early momentum.'
  },
  'ma-driven': {
    opening: 'This assessment supports M&A due diligence by identifying security risks and integration requirements.',
    roadmapIntro: 'The roadmap prioritizes risk identification and integration-critical security controls.',
    priorityFocus: 'Risk visibility, access controls, and data protection are emphasized for integration planning.'
  }
}

// Domain boost mappings for threat profile adjustments
const DRIVER_DOMAIN_BOOSTS = {
  'incident-response': {
    'security-operations': 1.5,
    'incident-response': 1.5,
    'detection-and-monitoring': 1.4,
    'endpoint-security': 1.3
  },
  'insurance': {
    'identity-and-access-management': 1.4,
    'backup-and-recovery': 1.5,
    'incident-response': 1.3,
    'endpoint-security': 1.3
  },
  'governance': {
    'governance-and-policy': 1.5,
    'risk-management': 1.4,
    'security-awareness': 1.2
  },
  'third-party': {
    'vendor-management': 1.5,
    'identity-and-access-management': 1.3,
    'data-protection': 1.4
  },
  'compliance': {
    'governance-and-policy': 1.4,
    'data-protection': 1.4,
    'audit-and-logging': 1.5
  },
  'proactive': {
    // Balanced - no specific boosts
  },
  'ma': {
    'identity-and-access-management': 1.5,
    'data-protection': 1.4,
    'vendor-management': 1.3,
    'risk-management': 1.3
  }
}

// Timeline-based urgency multipliers
const TIMELINE_URGENCY = {
  'immediate': { multiplier: 2.0, phase: 'immediate', label: 'Critical (0-30 days)' },
  'short-term': { multiplier: 1.5, phase: 'short-term', label: 'High Priority (1-3 months)' },
  'medium-term': { multiplier: 1.2, phase: 'medium-term', label: 'Planned (3-6 months)' },
  'long-term': { multiplier: 1.0, phase: 'long-term', label: 'Strategic (6-12 months)' },
  'ongoing': { multiplier: 1.0, phase: 'ongoing', label: 'Continuous Improvement' }
}

/**
 * Extract engagement driver context from business scoping responses
 * @param {Object} businessScopingResponses - Full business scoping responses including ed-* questions
 * @returns {Object} Engagement context with driver, timeline, and outcome data
 */
export function getEngagementDriverContext(businessScopingResponses) {
  if (!businessScopingResponses) {
    return {
      hasEngagementData: false,
      primaryDriver: null,
      timeline: null,
      desiredOutcome: null,
      tags: [],
      urgencyMultiplier: 1.0,
      narrativeHook: 'maturity-driven'
    }
  }

  const ed1Response = businessScopingResponses['ed-1']
  const ed2Response = businessScopingResponses['ed-2']
  const ed3Response = businessScopingResponses['ed-3']

  // Get the engagement context section from business scoping data
  const engagementSection = businessScopingData.sections.find(s => s.id === 'engagement-context')
  const ed1Question = engagementSection?.questions.find(q => q.id === 'ed-1')
  const ed2Question = engagementSection?.questions.find(q => q.id === 'ed-2')
  const ed3Question = engagementSection?.questions.find(q => q.id === 'ed-3')

  // Extract primary driver details (supports multi-select array or single value)
  const ed1Raw = typeof ed1Response === 'object' ? ed1Response.value : ed1Response
  const ed1Values = Array.isArray(ed1Raw) ? ed1Raw : (ed1Raw ? [ed1Raw] : [])
  const ed1Options = ed1Values
    .map(v => ed1Question?.options.find(o => o.label === v))
    .filter(Boolean)

  // Extract timeline details
  const ed2Value = typeof ed2Response === 'object' ? ed2Response.value : ed2Response
  const ed2Option = ed2Question?.options.find(o => o.label === ed2Value)

  // Extract desired outcome details (supports multi-select array or single value)
  const ed3Raw = typeof ed3Response === 'object' ? ed3Response.value : ed3Response
  const ed3Values = Array.isArray(ed3Raw) ? ed3Raw : (ed3Raw ? [ed3Raw] : [])
  const ed3Options = ed3Values
    .map(v => ed3Question?.options.find(o => o.label === v))
    .filter(Boolean)

  // Collect all tags from selected options (merge from all ed-1 and ed-3 selections)
  const ed1Tags = ed1Options.flatMap(o => o.tags || [])
  const ed3Tags = ed3Options.flatMap(o => o.tags || [])
  const tags = [
    ...ed1Tags,
    ...(ed2Option?.tags || []),
    ...ed3Tags
  ]

  // Use highest urgency multiplier from selected drivers
  const driverUrgency = ed1Options.length > 0
    ? Math.max(...ed1Options.map(o => o.urgencyMultiplier || 1.0))
    : 1.0
  const timelineUrgency = ed2Option?.urgencyMultiplier || 1.0
  const combinedUrgency = Math.min(driverUrgency * timelineUrgency, 3.0) // Cap at 3x

  // Use the highest-urgency driver's narrative hook as primary
  const primaryOption = ed1Options.length > 0
    ? ed1Options.reduce((a, b) => (b.urgencyMultiplier || 1.0) > (a.urgencyMultiplier || 1.0) ? b : a)
    : null

  return {
    hasEngagementData: !!(ed1Values.length > 0 || ed2Value || ed3Values.length > 0),
    primaryDriver: ed1Options.length > 0 ? {
      values: ed1Values,
      value: primaryOption?.label || ed1Values[0],
      tags: ed1Tags,
      urgencyMultiplier: driverUrgency,
      narrativeHook: primaryOption?.narrativeHook || 'maturity-driven',
      allHooks: ed1Options.map(o => o.narrativeHook).filter(Boolean)
    } : null,
    timeline: ed2Value ? {
      value: ed2Value,
      tags: ed2Option?.tags || [],
      urgencyMultiplier: ed2Option?.urgencyMultiplier || 1.0,
      roadmapPhase: ed2Option?.roadmapPhase || 'medium-term'
    } : null,
    desiredOutcome: ed3Options.length > 0 ? {
      values: ed3Values,
      value: ed3Values[0],
      tags: ed3Tags,
      outcomeType: ed3Options[0]?.outcomeType || 'roadmap',
      allOutcomeTypes: ed3Options.map(o => o.outcomeType).filter(Boolean)
    } : null,
    tags,
    urgencyMultiplier: combinedUrgency,
    narrativeHook: primaryOption?.narrativeHook || 'maturity-driven'
  }
}

/**
 * Generate report framing content based on engagement context
 * @param {Object} engagementContext - Output from getEngagementDriverContext
 * @returns {Object} Narrative content for report sections
 */
export function generateReportFraming(engagementContext) {
  if (!engagementContext?.hasEngagementData) {
    return {
      opening: NARRATIVE_TEMPLATES['maturity-driven'].opening,
      roadmapIntro: NARRATIVE_TEMPLATES['maturity-driven'].roadmapIntro,
      priorityFocus: NARRATIVE_TEMPLATES['maturity-driven'].priorityFocus,
      executiveSummaryPrefix: '',
      outcomeAlignment: ''
    }
  }

  const narrativeHook = engagementContext.narrativeHook || 'maturity-driven'
  const template = NARRATIVE_TEMPLATES[narrativeHook] || NARRATIVE_TEMPLATES['maturity-driven']

  // Build outcome alignment text (supports multiple outcomes)
  const outcomeTexts = {
    'gap-analysis': 'gap visibility and risk quantification',
    'roadmap': 'actionable recommendations with clear prioritization',
    'compliance-evidence': 'compliance documentation and evidence requirements',
    'executive-reporting': 'executive-ready summaries suitable for board presentation',
    'investment-validation': 'evaluation of current security investments',
    'program-foundation': 'a strategic foundation for building a sustainable security program'
  }

  let outcomeAlignment = ''
  if (engagementContext.desiredOutcome) {
    const outcomeTypes = engagementContext.desiredOutcome.allOutcomeTypes || [engagementContext.desiredOutcome.outcomeType]
    const parts = outcomeTypes.map(t => outcomeTexts[t]).filter(Boolean)
    if (parts.length === 1) {
      outcomeAlignment = `This report emphasizes ${parts[0]}.`
    } else if (parts.length > 1) {
      const last = parts.pop()
      outcomeAlignment = `This report emphasizes ${parts.join(', ')}, and ${last}.`
    }
  }

  // Build executive summary prefix based on urgency
  let executiveSummaryPrefix = ''
  if (engagementContext.urgencyMultiplier >= 2.0) {
    executiveSummaryPrefix = 'URGENT: '
  } else if (engagementContext.urgencyMultiplier >= 1.5) {
    executiveSummaryPrefix = 'HIGH PRIORITY: '
  }

  return {
    opening: template.opening,
    roadmapIntro: template.roadmapIntro,
    priorityFocus: template.priorityFocus,
    executiveSummaryPrefix,
    outcomeAlignment
  }
}

/**
 * Get roadmap overrides based on engagement context
 * @param {Object} engagementContext - Output from getEngagementDriverContext
 * @returns {Object} Urgency multipliers and phase adjustments for roadmap generation
 */
export function getRoadmapOverrides(engagementContext) {
  if (!engagementContext?.hasEngagementData) {
    return {
      urgencyMultiplier: 1.0,
      phaseCompression: false,
      priorityPhase: 'medium-term',
      domainBoosts: {},
      focusTags: []
    }
  }

  const timeline = engagementContext.timeline?.roadmapPhase || 'medium-term'
  const timelineConfig = TIMELINE_URGENCY[timeline] || TIMELINE_URGENCY['medium-term']

  // Determine domain boosts based on primary driver tags
  let domainBoosts = {}
  const driverTags = engagementContext.primaryDriver?.tags || []

  if (driverTags.includes('incident-response')) {
    domainBoosts = { ...domainBoosts, ...DRIVER_DOMAIN_BOOSTS['incident-response'] }
  }
  if (driverTags.includes('insurance')) {
    domainBoosts = { ...domainBoosts, ...DRIVER_DOMAIN_BOOSTS['insurance'] }
  }
  if (driverTags.includes('governance') || driverTags.includes('top-down')) {
    domainBoosts = { ...domainBoosts, ...DRIVER_DOMAIN_BOOSTS['governance'] }
  }
  if (driverTags.includes('third-party') || driverTags.includes('contractual')) {
    domainBoosts = { ...domainBoosts, ...DRIVER_DOMAIN_BOOSTS['third-party'] }
  }
  if (driverTags.includes('compliance') || driverTags.includes('regulatory')) {
    domainBoosts = { ...domainBoosts, ...DRIVER_DOMAIN_BOOSTS['compliance'] }
  }
  if (driverTags.includes('ma') || driverTags.includes('due-diligence')) {
    domainBoosts = { ...domainBoosts, ...DRIVER_DOMAIN_BOOSTS['ma'] }
  }

  // Determine if phase compression is needed (urgent timelines)
  const phaseCompression = engagementContext.urgencyMultiplier >= 1.5

  return {
    urgencyMultiplier: engagementContext.urgencyMultiplier,
    phaseCompression,
    priorityPhase: timelineConfig.phase,
    priorityPhaseLabel: timelineConfig.label,
    domainBoosts,
    focusTags: engagementContext.tags
  }
}

/**
 * Get threat profile adjustments based on engagement context
 * @param {Object} engagementContext - Output from getEngagementDriverContext
 * @returns {Object} Domain boosts and relevance adjustments for threat scoring
 */
export function getThreatProfileAdjustments(engagementContext) {
  if (!engagementContext?.hasEngagementData) {
    return {
      domainBoosts: {},
      threatCategoryBoosts: {},
      relevanceMultiplier: 1.0
    }
  }

  const driverTags = engagementContext.primaryDriver?.tags || []

  // Build domain boosts
  let domainBoosts = {}
  for (const [driverKey, boosts] of Object.entries(DRIVER_DOMAIN_BOOSTS)) {
    const relevantTags = {
      'incident-response': ['incident-response', 'urgent', 'reactive'],
      'insurance': ['insurance', 'compliance', 'external-pressure'],
      'governance': ['governance', 'top-down', 'strategic'],
      'third-party': ['third-party', 'contractual', 'external-pressure'],
      'compliance': ['compliance', 'regulatory', 'deadline'],
      'ma': ['ma', 'due-diligence', 'urgent']
    }

    if (relevantTags[driverKey]?.some(tag => driverTags.includes(tag))) {
      domainBoosts = { ...domainBoosts, ...boosts }
    }
  }

  // Build threat category boosts based on driver
  const threatCategoryBoosts = {}
  if (driverTags.includes('incident-response') || driverTags.includes('urgent')) {
    threatCategoryBoosts['ransomware'] = 1.4
    threatCategoryBoosts['data-breach'] = 1.3
  }
  if (driverTags.includes('insurance') || driverTags.includes('compliance')) {
    threatCategoryBoosts['ransomware'] = threatCategoryBoosts['ransomware'] || 1.2
    threatCategoryBoosts['business-email-compromise'] = 1.3
  }
  if (driverTags.includes('third-party') || driverTags.includes('contractual')) {
    threatCategoryBoosts['supply-chain'] = 1.4
    threatCategoryBoosts['data-breach'] = 1.3
  }

  return {
    domainBoosts,
    threatCategoryBoosts,
    relevanceMultiplier: engagementContext.urgencyMultiplier
  }
}

/**
 * Get a summary of engagement context for display
 * @param {Object} engagementContext - Output from getEngagementDriverContext
 * @returns {Object} Human-readable summary for UI display
 */
export function getEngagementSummary(engagementContext) {
  if (!engagementContext?.hasEngagementData) {
    return {
      hasData: false,
      primaryDriverLabel: 'Not specified',
      timelineLabel: 'Not specified',
      outcomeLabel: 'Not specified',
      urgencyLevel: 'Standard',
      tagSummary: []
    }
  }

  // Determine urgency level label
  let urgencyLevel = 'Standard'
  if (engagementContext.urgencyMultiplier >= 2.0) {
    urgencyLevel = 'Critical'
  } else if (engagementContext.urgencyMultiplier >= 1.5) {
    urgencyLevel = 'High'
  } else if (engagementContext.urgencyMultiplier >= 1.2) {
    urgencyLevel = 'Elevated'
  }

  const driverValues = engagementContext.primaryDriver?.values || []
  const primaryDriverLabel = driverValues.length > 0
    ? driverValues.join('; ')
    : (engagementContext.primaryDriver?.value || 'Not specified')

  return {
    hasData: true,
    primaryDriverLabel,
    primaryDriverValues: driverValues,
    timelineLabel: engagementContext.timeline?.value || 'Not specified',
    outcomeLabel: (engagementContext.desiredOutcome?.values || []).join('; ') || engagementContext.desiredOutcome?.value || 'Not specified',
    outcomeValues: engagementContext.desiredOutcome?.values || [],
    urgencyLevel,
    urgencyMultiplier: engagementContext.urgencyMultiplier,
    tagSummary: [...new Set(engagementContext.tags)].slice(0, 8) // Unique tags, max 8
  }
}
